import { Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ImageAssetsService } from '@services/assets/image-assets.service';
import { VideoAssetsService } from '@services/assets/video-assets.service';

import { SlideConfigComponent } from './slide-config/slide-config.component';
import { EnvironmentService } from '@services/environment/environment.service';
import { IbexCheckboxComponent } from '../../../../../components/ibex-checkbox/ibex-checkbox.component';

@Component({
    selector: 'slide-items',
    templateUrl: './slide-items.component.html',
    styleUrls: ['./slide-items.component.scss'],
    providers: [ImageAssetsService, VideoAssetsService],
    standalone: false
})
export class SlideItemsComponent implements OnInit {

  @Input() dynamicContent;
  @Input() translationEntries;
  @Input() lang;
  @ViewChild('checkboxAll') checkboxAll: IbexCheckboxComponent;
  @ViewChildren(IbexCheckboxComponent) checkboxes!: QueryList<IbexCheckboxComponent>;

  public imageAssetPath: any;
  public videoPath: any;
  public slideMap: Map<number, { slide: boolean, fileIndex: string }> = new Map();
  public timestamp = Date.now();
  public isTranslation = false;
  selectedItems2 = [];
  assetsByPath = {};

  checkBoxActions = [{
    translation: 'dynamicContent.disconnectMultipleItems', icon: 'delete', action: () => { this.disconnectSelectedItems() }
  }];

  constructor(
    private dialog: MatDialog, private imageAssetsService: ImageAssetsService, private videoAssetsService: VideoAssetsService, private environmentService: EnvironmentService) {
    this.imageAssetPath = this.environmentService.getImageAssetPath();
    this.videoPath = this.environmentService.getVideoAssetPath();
  }

  async ngOnInit() {
    this.isTranslation = this.lang !== this.dynamicContent.lang;
    this.initData(this.isTranslation ? this.translationEntries[this.lang] : this.dynamicContent);
    const paths = this.dynamicContent.content.slides.map(el => el.value);
    const images = (await this.imageAssetsService.find(0, paths.length, { path: { $in: paths } })).data as any[];
    const videos = (await this.videoAssetsService.find(0, paths.length, { path: { $in: paths } })).data as any[];
    for (const element of images.concat(videos)) {
      this.assetsByPath[element.path] = element
    }
    this.repairVideoDurations();
  }

  async repairVideoDurations() {
    const items = this.dynamicContent.content.slides.filter(el => el.duration === 0 && el.type === 'video');
    for (let element of items) {
      const duration = await this.getVideoDuration(this.videoPath + element.value)
      element.duration = Math.round(duration);
      this.videoAssetsService.patch(this.assetsByPath[element.value]._id, { duration: Math.round(duration) });
    }
  }

  async getVideoDuration(path): Promise<number> {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = () => {
        reject(new Error("Failed to load video metadata"));
      };
      video.src = path;
    });
  }

  initData(entry) {
    if (!entry.content.slides) {
      entry.content['slides'] = [];
    }
    this.selectedItems2 = [];
    entry.content.slides.forEach((slide, i) => {
      if (!entry.mediaFiles.find(el => el.url === slide.value)) {
        entry.mediaFiles.push({ url: slide.value, mimeType: slide.type === 'video' ? 'video/mp4' : 'image/jpeg', name: slide?.value?.split('/').pop() });
      }
      this.slideMap[i] = { slide: slide, fileIndex: slide.type === 'website' ? null : entry.mediaFiles.findIndex(file => file.url === slide.value) };
      this.selectedItems2.push({ index: i, slide, selected: false });
    });
  }

  async addSlide() {
    const dialogRef: MatDialogRef<SlideConfigComponent> = this.dialog.open(SlideConfigComponent, { width: '448px', panelClass: 'slide-config-modal' });
    const slideObject = await dialogRef.afterClosed().toPromise();
    if (!slideObject || !slideObject.slide) {
      return;
    }
    this.addSlideData(this.isTranslation ? this.translationEntries[this.lang] : this.dynamicContent, slideObject);
    this.ngOnInit();
  }

  addSlideData(entry, slideObject) {
    entry.content.slides.push(slideObject.slide);
    if (!entry.mediaFiles) {
      entry.mediaFiles = [];
    }
    const index = entry.mediaFiles.findIndex(file => { return file.url === slideObject.slide.value });
    if (slideObject.file && index === -1) {
      entry.mediaFiles.push(slideObject.file);
    }
  }

  public removeSlide(index) {
    const fileIndex = this.slideMap[index].fileIndex;
    if (this.slideMap[index].fileIndex >= 0) {
      (this.isTranslation ? this.translationEntries[this.lang] : this.dynamicContent).content.slides.splice(index, 1);
      if (Object.keys(Object.fromEntries(Object.entries(this.slideMap).filter(([_, value]) => value.fileIndex === fileIndex))).length === 1) {
        (this.isTranslation ? this.translationEntries[this.lang] : this.dynamicContent).mediaFiles.splice(this.slideMap[index].fileIndex, 1);
      }
    }
    this.ngOnInit();
  }

  async editSlide(index) {
    const item = this.dynamicContent.content.slides[index];
    const dialogRef: MatDialogRef<SlideConfigComponent> = this.dialog.open(SlideConfigComponent, { width: '448px', panelClass: 'slide-config-modal', data: { item } });
    const slideObject = await dialogRef.afterClosed().toPromise();
    if (!slideObject) {
      return;
    }
    if (this.isTranslation) {
      this.translationEntries[this.lang].content.slides[index] = slideObject.slide;
      if (!this.translationEntries[this.lang].mediaFiles) {
        this.translationEntries[this.lang].mediaFiles = [];
      }
    } else {
      this.dynamicContent.content.slides[index] = slideObject.slide;
    }
  }

  public dropItem(event: any) {
    if (event.previousIndex !== event.currentIndex) {
      let selectedItem = this.selectedItems2.splice(event.previousIndex, 1)[0];
      this.selectedItems2.splice(event.currentIndex, 0, selectedItem);
      const item = (this.isTranslation ? this.translationEntries[this.lang] : this.dynamicContent).content.slides.splice(event.previousIndex, 1)[0];
      (this.isTranslation ? this.translationEntries[this.lang] : this.dynamicContent).content.slides.splice(event.currentIndex, 0, item);
    }
  }

  public disconnectSelectedItems() {
    for (const element of this.selectedItems2.filter(el => !!el.selected).reverse()) {
      this.dynamicContent.mediaFiles.splice(this.dynamicContent.mediaFiles.findIndex(el => el.url === element.url), 1);
      (this.isTranslation ? this.translationEntries[this.lang] : this.dynamicContent).content.slides.splice(element.index, 1);
    }
    this.selectedItems2 = (this.isTranslation ? this.translationEntries[this.lang] : this.dynamicContent).content.slides.map(slide => {
      return { index: (this.isTranslation ? this.translationEntries[this.lang] : this.dynamicContent).content.slides.findIndex(s => s === slide), slide, selected: false };
    })
    this.checkboxes.forEach(checkbox => { checkbox.unselect() });
  }

  public selectItem(index: any) {
    this.selectedItems2[index].selected = !this.selectedItems2[index].selected;
    this.checkboxAll[!!this.selectedItems2.find(el => !!el.selected) ? 'select' : 'unselect']();
  }

  public selectItemsAll() {
    const isSelected = !!this.getSelectionCount();
    this.selectedItems2.forEach(el => el.selected = !isSelected);
    this.checkboxes.filter(el => el.id !== 'checkboxAll').forEach(checkbox => { checkbox[isSelected ? 'unselect' : 'select']() });
  }

  public getSelectionCount() {
    return this.selectedItems2.filter(el => !!el.selected)?.length;
  }

}
